import { AlgorithmResult, OptimizationResults, ParetoAnalysis, ParetoAnalysisResult } from '../types';

/**
 * Solution with algorithm and run identification
 */
export interface SolutionWithMetadata {
  algorithmName: string;
  algorithmKey: string;
  runId: number;
  fitness: number;
  energy: number;
  cost: number;
  feasible?: boolean; // Optional feasibility flag
}

/**
 * Checks if solution A dominates solution B
 * A dominates B if:
 * - A.fitness >= B.fitness (maximize fitness)
 * - A.energy <= B.energy (minimize energy)
 * - A.cost <= B.cost (minimize cost)
 * - At least one inequality is strict
 */
export function dominates(a: SolutionWithMetadata, b: SolutionWithMetadata): boolean {
  const fitnessBetter = a.fitness >= b.fitness;
  const energyBetter = a.energy <= b.energy;
  const costBetter = a.cost <= b.cost;
  
  const allBetterOrEqual = fitnessBetter && energyBetter && costBetter;
  const atLeastOneStrict = 
    a.fitness > b.fitness || 
    a.energy < b.energy || 
    a.cost < b.cost;
  
  return allBetterOrEqual && atLeastOneStrict;
}

/**
 * Finds all Pareto-optimal solutions from a set of solutions
 * @param solutions - Array of solutions to analyze
 * @returns Array of Pareto-optimal solutions
 */
export function findParetoOptimal(solutions: SolutionWithMetadata[]): SolutionWithMetadata[] {
  if (solutions.length === 0) return [];
  
  const paretoOptimal: SolutionWithMetadata[] = [];
  
  for (const candidate of solutions) {
    let isDominated = false;
    
    for (const other of solutions) {
      if (candidate === other) continue;
      
      if (dominates(other, candidate)) {
        isDominated = true;
        break;
      }
    }
    
    if (!isDominated) {
      paretoOptimal.push(candidate);
    }
  }
  
  return paretoOptimal;
}

/**
 * Performs Pareto analysis across all algorithms and runs
 * @param allRuns - Array of results from all runs
 * @returns Pareto analysis results
 */
export function performParetoAnalysis(
  allRuns: OptimizationResults[]
): ParetoAnalysis {
  const algorithmKeys = ['ga', 'culture', 'frog', 'sflaga', 'sflaca', 'pso', 'gapso'] as const;
  const algorithmNames = ['GA', 'Culture', 'Frog', 'SFLAGA', 'SFLACA', 'PSO', 'GAPSO'];
  
  // Convert all runs to solutions with metadata
  const allSolutions: SolutionWithMetadata[] = [];
  
  allRuns.forEach((run, runId) => {
    algorithmKeys.forEach((algorithmKey, idx) => {
      const result = run[algorithmKey];
      if (result) {
        allSolutions.push({
          algorithmName: algorithmNames[idx],
          algorithmKey,
          runId,
          fitness: result.fitness,
          energy: result.energy,
          cost: result.cost,
          feasible: true, // Assume feasible if not specified
        });
      }
    });
  });
  
  // Find global Pareto front
  const globalParetoFront = findParetoOptimal(allSolutions);
  
  // Count Pareto-optimal solutions per algorithm
  const algorithmCounts: { [key: string]: number } = {};
  algorithmKeys.forEach((key) => {
    algorithmCounts[key] = 0;
  });
  
  globalParetoFront.forEach((solution) => {
    algorithmCounts[solution.algorithmKey]++;
  });
  
  // Calculate percentage contribution per algorithm
  const totalParetoOptimal = globalParetoFront.length;
  const algorithmContributions: ParetoAnalysisResult[] = algorithmKeys.map((key, idx) => {
    const count = algorithmCounts[key];
    const percentage = totalParetoOptimal > 0 
      ? (count / totalParetoOptimal) * 100 
      : 0;
    
    return {
      algorithmName: algorithmNames[idx],
      paretoOptimalCount: count,
      percentageContribution: percentage,
    };
  });
  
  return {
    globalParetoFront: globalParetoFront.map((sol) => ({
      algorithmName: sol.algorithmName,
      runId: sol.runId,
      fitness: sol.fitness,
      energy: sol.energy,
      cost: sol.cost,
    })),
    algorithmContributions,
    totalSolutions: allSolutions.length,
    paretoOptimalSolutions: totalParetoOptimal,
  };
}

